import { useMutation, useQueryClient } from 'react-query';
import {
  acceptTransferRequest,
  addContact,
  cancelAssociateTransferRequest,
  createAssociateTransferRequest,
  declineAssociateEvent,
  declineTransferRequest,
  httpClient,
  readAssociateEvent,
} from './heroApiClient';
import {
  Associate,
  AuthenticateEmailResponse,
  AuthenticateInviteCodeResponse,
  LoginFirebasePayload,
  LoginPayload,
  LogoutPayload,
  OnboardingSteps,
  OutgoingTransferFormErrors,
  RegisterAssociatePayload,
  UpdateAssociatePayload,
} from '../../model';
import { AxiosError } from 'axios';

export const usePerformLogin = () => {
  return useMutation(['performLogin'], (payload: LoginPayload) =>
    httpClient.post<{ token: string }>('/auth/login', payload),
  );
};

export const usePerformLoginFirebase = () => {
  return useMutation(['performLoginFirebase'], (payload: LoginFirebasePayload) =>
    httpClient.post<{ token: string }>('/auth/login-firebase-token', {
      ...payload,
      accountType: 'associate',
    }),
  );
};

export const usePerformLogout = () => {
  return useMutation(['performLogout'], (payload: LogoutPayload) =>
    httpClient.patch('/associates/logout', payload).catch((e) => {
      console.error('logout request failed:', e); // However, the logout logics in AuthenticationContext provider should mange to clear and navigate
    }),
  );
};

export const useRegisterAssociate = () => {
  return useMutation(
    ['registerAssociate'],
    (payload: RegisterAssociatePayload) =>
      httpClient.post('/auth/register', payload),
  );
};

export const useUpdateAssociate = () => {
  return useMutation<{ data: Associate }, unknown, UpdateAssociatePayload>(
    ['updateAssociate'],
    (payload: UpdateAssociatePayload) => httpClient.put('/users/me', payload),
  );
};

type CompleteAssociateSetupStepPayload = {
  onboardingStep: OnboardingSteps;
  introModuleId?: string;
};

export const useCompleteAssociateOnboardingStep = () => {
  return useMutation(
    ['completeAssociateOnboardingStep'],
    ({ onboardingStep, introModuleId }: CompleteAssociateSetupStepPayload) =>
      httpClient.put('/onboarding/v2/state', {
        [onboardingStep]: true,
        introModuleId,
      }),
    {},
  );
};

export const useChangeAssociateAvailability = () => {
  const queryClient = useQueryClient();
  return useMutation(
    ['changeAssociateAvailability'],
    (available: boolean) =>
      httpClient.put('/conversations/change-availability', { available }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['getAssociate']);
      },
    },
  );
};

export const useReadAssociateEvent = () => {
  return useMutation(['readAssociateEvent'], (eventId: string) =>
    readAssociateEvent(eventId),
  );
};

export const useDeclineAssociateEvent = () => {
  return useMutation(
    ['declineAssociateEvent'],
    ({ eventId, goUnavailable }: { eventId: string; goUnavailable: boolean }) =>
      declineAssociateEvent({ eventId, goUnavailable }),
  );
};

export const useDeclineTransferRequest = () => {
  return useMutation(['declineTransferRequest'], (transferRequestId: string) =>
    declineTransferRequest(transferRequestId),
  );
};

export const useAcceptTransferRequest = () => {
  return useMutation(['acceptTransferRequest'], (transferRequestId: string) =>
    acceptTransferRequest(transferRequestId),
  );
};

export const useCreateAssociateTransferRequest = () => {
  return useMutation(
    ['createTransferRequest'],
    async (payload: {
      associateIds: string[];
      conversationId: string;
      note: string;
    }) => {
      try {
        return await createAssociateTransferRequest(payload);
      } catch (e) {
        if (!(e instanceof AxiosError)) {
          throw e;
        }
        //1710 is the error code returned by the backend when all requested associates are offline
        if (e.response?.status === 400 && e.response?.data?.code === 1710) {
          return Promise.reject({
            error: OutgoingTransferFormErrors.ALL_ASSOCIATES_OFFLINE,
          });
        }
      }
    },
  );
};

export const useCancelAssociateTransferRequest = () => {
  return useMutation(['cancelTransferRequest'], (transferId: string) =>
    cancelAssociateTransferRequest(transferId),
  );
};

export const useAuthenticateInviteCode = () => {
  return useMutation(['authenticateInviteCode'], (code: string) =>
    httpClient.post<AuthenticateInviteCodeResponse>('/auth/invite', { code }),
  );
};

export const useAuthenticateEmail = () => {
  return useMutation(['authenticateEmail'], (email: string) =>
    httpClient.post<AuthenticateEmailResponse>('/auth/email', { email }),
  );
};

export const useAddContact = () => {
  return useMutation(['addContact'], (customerId: string) => addContact(customerId));
};
